import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Upload, Paperclip } from "lucide-react";
import { api, errMsg, inr, fmtDate, download } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Money } from "@/components/common";
import { BillMatch } from "@/components/BillMatch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function NewInvoice({ open, setOpen, onDone }) {
  const { parties } = useApp();
  const [f, setF] = useState({ party_id: "", invoice_no: "", date: "", amount: "", kind: "sales" });
  const save = async () => {
    try { await api.post("/invoices", f); toast.success("Invoice added"); setOpen(false); onDone(); } catch (e) { toast.error(await errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="invoice-dialog">
        <DialogHeader><DialogTitle>Add outstanding invoice</DialogTitle></DialogHeader>
        <Select value={f.party_id} onValueChange={(v) => setF({ ...f, party_id: v })}><SelectTrigger data-testid="invoice-party-select"><SelectValue placeholder="Party" /></SelectTrigger>
          <SelectContent>{parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
        <Input placeholder="Invoice number" value={f.invoice_no} onChange={(e) => setF({ ...f, invoice_no: e.target.value })} data-testid="invoice-no-input" />
        <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="invoice-date-input" />
        <Input placeholder="Amount (₹)" className="num" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="invoice-amount-input" />
        <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}><SelectTrigger data-testid="invoice-kind-select"><SelectValue /></SelectTrigger>
          <SelectContent>{[["sales", "Sales invoice"], ["purchase", "Purchase bill"], ["credit_note", "Credit note"], ["debit_note", "Debit note"]].map(([a, b]) => <SelectItem key={a} value={a}>{b}</SelectItem>)}</SelectContent></Select>
        <Button onClick={save} data-testid="invoice-save-button">Save</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function Bills() {
  const { can } = useApp();
  const [inv, setInv] = useState([]);
  const [txns, setTxns] = useState([]);
  const [open, setOpen] = useState(false);
  const [match, setMatch] = useState(null);
  const [attachFor, setAttachFor] = useState(null);
  const csv = useRef();
  const file = useRef();
  const load = async () => {
    const [a, b] = await Promise.all([api.get("/invoices"), api.get("/transactions", { params: { status: "classified,approved", limit: 500 } })]);
    setInv(a.data);
    const partyIds = new Set(a.data.filter((i) => Number(i.outstanding) > 0).map((i) => i.party_id));
    setTxns(b.data.items.filter((t) => t.party_id && partyIds.has(t.party_id) && !(t.bill_allocations || []).length && !["exported", "imported"].includes(t.export_status)));
  };
  useEffect(() => { load(); }, []);
  const importCsv = async (f) => {
    const fd = new FormData(); fd.append("file", f);
    try { const r = await api.post("/invoices/import", fd); toast.success(`${r.data.created} invoices imported${r.data.errors.length ? `, ${r.data.errors.length} rejected (line ${r.data.errors[0].line}: ${r.data.errors[0].error})` : ""}`); load(); }
    catch (e) { toast.error(await errMsg(e)); }
  };
  const attach = async (f) => {
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/invoices/${attachFor}/attachment`, fd); toast.success("Attachment stored securely"); load(); } catch (e) { toast.error(await errMsg(e)); }
  };
  return (
    <div>
      <PageHeader title="Bill Matching" sub="Match bank receipts and payments to invoices — single, multiple, part payments, advances and on-account. Suggestions always need your confirmation.">
        {can("admin", "accountant") && <>
          <Button variant="outline" onClick={() => csv.current.click()} data-testid="import-invoices-button"><Upload className="mr-2 h-4 w-4" />Import outstanding (CSV)</Button>
          <Button onClick={() => setOpen(true)} data-testid="new-invoice-button"><Plus className="mr-2 h-4 w-4" />Add invoice</Button></>}
        <input ref={csv} type="file" accept=".csv" className="hidden" onChange={(e) => { importCsv(e.target.files[0]); e.target.value = ""; }} />
        <input ref={file} type="file" accept=".pdf,.png,.jpg" className="hidden" onChange={(e) => { attach(e.target.files[0]); e.target.value = ""; }} />
      </PageHeader>
      <div className="mb-3 text-xs text-muted-foreground">CSV columns: party, invoice_no, date, amount, kind (sales/purchase/credit_note/debit_note), outstanding — e.g. a Tally "Bills Outstanding" export.</div>
      <div className="grid gap-6 xl:grid-cols-2">
        <div>
          <h3 className="mb-3 font-semibold">Unallocated receipts & payments ({txns.length})</h3>
          <div className="panel max-h-[60vh] overflow-auto">
            {txns.map((t) => (
              <div key={t.id} className="flex items-center gap-3 border-b p-3 text-xs" data-testid="unallocated-txn">
                <span className="num">{fmtDate(t.txn_date)}</span><span className="flex-1 truncate">{t.party_name}<div className="truncate font-mono text-muted-foreground">{t.narration}</div></span>
                <Money v={t.amount} dir={t.direction} /><Button size="sm" variant="outline" onClick={() => setMatch(t)} data-testid="match-bills-button">Match</Button>
              </div>
            ))}
            {txns.length === 0 && <div className="p-6 text-sm text-muted-foreground">Nothing to match. Classified transactions for parties with open invoices appear here.</div>}
          </div>
        </div>
        <div>
          <h3 className="mb-3 font-semibold">Invoices ({inv.length})</h3>
          <div className="panel max-h-[60vh] overflow-auto">
            <table className="w-full text-xs"><thead className="glass sticky top-0 text-left text-muted-foreground"><tr>{["Invoice", "Party", "Date", "Amount", "Outstanding", ""].map((h) => <th key={h} className="p-2.5 font-medium">{h}</th>)}</tr></thead>
              <tbody>{inv.map((i) => (
                <tr key={i.id} className="border-t" data-testid="invoice-row">
                  <td className="p-2.5">{i.invoice_no}<div className="text-[10px] text-muted-foreground">{i.kind}</div></td><td className="p-2.5">{i.party_name}</td><td className="num p-2.5">{fmtDate(i.date)}</td>
                  <td className="num p-2.5">{inr(i.amount)}</td><td className={`num p-2.5 ${Number(i.outstanding) > 0 ? "text-amber-500" : "text-emerald-500"}`}>{inr(i.outstanding)}</td>
                  <td className="p-2.5">
                    {(i.attachments || []).map((a) => <button key={a.id} className="mr-1 text-primary hover:underline" onClick={() => download(`/invoices/${i.id}/attachment/${a.id}`)}>{a.filename.slice(0, 12)}</button>)}
                    {can("admin", "accountant") && <button onClick={() => { setAttachFor(i.id); file.current.click(); }} title="Attach invoice PDF" data-testid="attach-invoice-button"><Paperclip className="inline h-3.5 w-3.5" /></button>}
                  </td>
                </tr>))}</tbody></table>
            {inv.length === 0 && <div className="p-6 text-sm text-muted-foreground">No invoices yet. Import outstanding bills from Tally or add them manually.</div>}
          </div>
        </div>
      </div>
      <NewInvoice open={open} setOpen={setOpen} onDone={load} />
      <Dialog open={!!match} onOpenChange={(o) => !o && setMatch(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Match {match?.party_name} · {inr(match?.amount)}</DialogTitle></DialogHeader>
          {match && <BillMatch txn={match} onDone={() => { setMatch(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
